import { TrackingEventEnum } from "@booking/eventEnums";
import { type Path, BOOKING } from "@booking/paths";
import type { AllOrNone, OneOrMore } from "@booking/types";

import { installTrackingDebug, isGaDebugEnabled } from "@/utils/trackingDebug";

import { underscoreKeys } from "./helpers";

const DATA_PREFIX = "data-ga4-";

const EVENT_GROUPS_BY_PATH = Object.freeze({
  [BOOKING.PAX]: "Pax step",
  [BOOKING.DATE]: "Date step",
  [BOOKING.ROOMS]: "Rooms step",
  [BOOKING.AUTOTIMES]: "Auto times step",
  [BOOKING.TIMES]: "Tee times step",
  [BOOKING.SIMPLE_TEE_TIMES]: "Simple tee times step",
  [BOOKING.DETAILS]: "Booker details step",
  [BOOKING.PAYMENT]: "Payment step",
  [BOOKING.COMPLETE]: "Complete step",
  [BOOKING.ENQUIRY]: "Enquiry step",
  [BOOKING.ENQUIRY_COMPLETE]: "Enquiry complete step",
} as Record<Path, string>);

interface CommonOptionalParams {
  eventGroup?: string;
}

interface EventParams
  extends Record<
      string,
      string | number | boolean | undefined | EventParams | EventParams[]
    >,
    CommonOptionalParams {}

type EcommerceParams = EventParams & {
  items: EcommerceItem[];
} & AllOrNone<{
    currency: "GBP" | "EUR";
    value: number;
  }>;

// See e.g. https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase_item
type EcommerceItem = EventParams & {
  affiliation: string;
  coupon?: string;
  discount?: number;
  index?: number;
  itemBrand?: "YGT";
  itemCategory?: string;
  itemCategory2?: string;
  itemCategory3?: string;
  itemCategory4?: string;
  itemCategory5?: string;
  itemListName?: string;
  itemVariant?: string;
  price?: number;
  quantity: number;
} & OneOrMore<{
    itemId: string;
    itemName: string;
  }>;

interface Event extends EventParams {
  event: string;
}

interface EcommerceEvent extends EventParams {
  event: string;
  ecommerce: EcommerceParams;
}

type GaAddPaymentInfoParams = EcommerceParams & {
  paymentType: "Credit card"; // Eventually `| "Klarna" | "Voucher"` etc...
};

interface GaClickParams extends CommonOptionalParams {
  click?: string;
  clickTarget?: string;
  index?: number;
  linkText?: string;
  linkUrl?: string;
}

interface GaDialogParams extends CommonOptionalParams {
  id: string;
}

type GaPurchaseParams = EcommerceParams & {
  transactionId: string;
};

interface GaSelectContentParams extends CommonOptionalParams {
  contentType: string;
  index?: number;
  itemListName?: string;
  itemName: string;
}

interface GaVirtualPageViewParams extends CommonOptionalParams {
  pageLocation: string;
  pageTitle: string;
}

interface GaWarningParams extends CommonOptionalParams {
  message: string;
}

enum EventTypes {
  DYNAMIC_REMARKETING = "dynamicRemarketing",
  GA = "GAEvent",
  GA_NO_HIT = "GAEventNoHit",
  GA4 = "GA4Event",
  GA4_NO_HIT = "GA4EventNoHit",
  VIRTUAL_PAGEVIEW = "VirtualPageview",
}

function gaAddPaymentInfo(params: GaAddPaymentInfoParams): void {
  pushGaEcommerceEvent({ event: "add_payment_info", ecommerce: params });
}

function gaAddShippingInfo(params: EcommerceParams): void {
  pushGaEcommerceEvent({ event: "add_shipping_info", ecommerce: params });
}

function gaAddToCart(params: EcommerceParams): void {
  pushGaEcommerceEvent({ event: "add_to_cart", ecommerce: params });
}

function gaBeginCheckout(params: EcommerceParams): void {
  pushGaEcommerceEvent({ event: "begin_checkout", ecommerce: params });
}

function gaViewItemList(params: EcommerceParams): void {
  pushGaEcommerceEvent({ event: "view_item_list", ecommerce: params });
}

function gaClick(params: GaClickParams): void {
  for (let key in params) {
    if (params[key as keyof GaClickParams] === undefined) {
      delete params[key as keyof GaClickParams];
    }
  }
  pushGaEvent({ event: "click", ...params });
}

function gaClickAttrs(params: GaClickParams = {}): EventParams {
  const { click, clickTarget, eventGroup, index } = params;

  return {
    [`${DATA_PREFIX}click`]: click?.endsWith("_click") && click,
    [`${DATA_PREFIX}click-target`]: clickTarget,
    [`${DATA_PREFIX}event-group`]: eventGroup,
    [`${DATA_PREFIX}index`]: index?.toString(),
  };
}

function gaDialogShown(params: GaDialogParams): void {
  const { id, ...optionalParams } = params;

  pushGaEvent({ event: "dialog_shown", id, ...optionalParams });
}

function gaGenerateLead(params: EcommerceParams): void {
  const { value: lead_value } = params;
  pushGaEvent({ event: "generate_lead", lead_type: "Enquiry", lead_value });
}

function gaPurchase(params: GaPurchaseParams): void {
  pushGaEcommerceEvent({ event: "purchase", ecommerce: params });
}

function gaRemoveFromCart(params: EcommerceParams): void {
  pushGaEcommerceEvent({ event: "remove_from_cart", ecommerce: params });
}

function gaSelectContent(params: GaSelectContentParams): void {
  const { contentType, itemName, ...optionalParams } = params;

  pushGaEvent({
    event: "select_content",
    contentType,
    itemName,
    ...optionalParams,
  });
}

function gaVirtualPageView(params: GaVirtualPageViewParams): void {
  pushGaEvent({ event: "virtual_page_view", ...params });
}

function gaWarningShown(params: GaWarningParams): void {
  const { message, ...optionalParams } = params;

  pushGaEvent({
    event: "warning_shown",
    message: message.replace(/\s+/g, " ").trim(),
    ...optionalParams,
  });
}

function pushGaEcommerceEvent({ event, ecommerce }: EcommerceEvent): void {
  const defaultItemParams: Pick<EcommerceItem, "itemBrand"> = {
    itemBrand: "YGT",
  };
  const items: EcommerceItem[] = ecommerce.items.map((item) => ({
    ...defaultItemParams,
    ...item,
  }));

  pushGaEvent({ event, ecommerce: { ...ecommerce, items } });
}

function pushGaEvent(event: Event): void {
  installTrackingDebug();

  const payload = underscoreKeys({
    ...event,
    eventType: "GA4Event",
  } as Event) as Event;

  const { parent } = window;

  if (isGaDebugEnabled()) {
    console.log("[Tracking:booking] pushGaEvent", {
      event: payload.event,
      parentIsWindow: parent === window,
      payload,
    });
  }

  if (parent !== window) {
    parent.postMessage(
      {
        eventType: TrackingEventEnum.PUSH_TO_DATALAYER,
        event: payload,
      },
      import.meta.env.VITE_YGT_ORIGIN,
    );
  } else {
    pushGa4Event(payload);
  }

  if (isGaDebugEnabled()) {
    console.log("[GA Event]", payload);
  }
}

const pushGa4Event = (payload: Event): void => {
  window.dataLayer = window.dataLayer || [];

  if (isGaDebugEnabled()) {
    console.log("[Tracking:booking] pushGa4Event", {
      event: payload.event,
      dataLayerLengthBefore: window.dataLayer.length,
      payload,
    });
  }

  if (payload.ecommerce) window.dataLayer.push({ ecommerce: null }); // Recommended per docs
  window.dataLayer.push(payload);

  // Nullify `event_type` to prevent recursive merge issues on successive data layer
  // pushes from other sources. E.g. a third-party push to the data layer could
  // inadvertently trigger GA4 tags in GTM if this param is still present.
  window.dataLayer.push({ event_type: null });
};

const gaEventNoHit = (
  eventCategory: string,
  eventAction: string,
  eventLabel: string,
) => {
  pushGa4Event({
    event: EventTypes.GA_NO_HIT,
    eventCategory,
    eventAction,
    eventLabel,
  });
  pushGa4Event({
    event: EventTypes.GA4_NO_HIT,
    eventCategory,
    eventAction,
    eventLabel,
  });
};

export {
  EVENT_GROUPS_BY_PATH,
  gaAddPaymentInfo,
  gaAddShippingInfo,
  gaAddToCart,
  gaClick,
  gaClickAttrs,
  gaSelectContent,
  gaBeginCheckout,
  gaDialogShown,
  gaGenerateLead,
  gaPurchase,
  gaRemoveFromCart,
  gaVirtualPageView,
  gaWarningShown,
  gaViewItemList,
  gaEventNoHit,
  pushGa4Event,
  type EcommerceItem,
  type EcommerceParams,
  type GaClickParams,
  type GaSelectContentParams,
  type GaWarningParams,
};
